var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
const STATUS_API_URL = "https://status.flatfile.io/api/v2/components.json";
const POLLING_INTERVAL = 15e3;
const INITIAL_BACKOFF_DELAY = 1e3;
const MAX_BACKOFF_DELAY = 3e4;
const BACKOFF_MULTIPLIER = 2;
const REGIONAL_COMPONENT_MAPPING = {
  "platform.flatfile.com": {
    "spaces-ui": ["Spaces"],
    "new-dashboard": ["Dashboard"]
  },
  "platform.ca.flatfile.com": {
    "spaces-ui": ["CA Spaces"],
    "new-dashboard": ["CA Dashboard"]
  },
  "platform.eu.flatfile.com": {
    "spaces-ui": ["EU Spaces"],
    "new-dashboard": ["EU Dashboard"]
  },
  "platform.uk.flatfile.com": {
    "spaces-ui": ["UK Spaces"],
    "new-dashboard": ["UK Dashboard"]
  },
  "platform.au.flatfile.com": {
    "spaces-ui": ["AU Spaces"],
    "new-dashboard": ["AU Dashboard"]
  },
  localhost: {
    "spaces-ui": ["Spaces"],
    "new-dashboard": ["Dashboard"]
  }
};
class StatusService {
  constructor() {
    __publicField(this, "checkInterval", null);
    __publicField(this, "isMonitoring", false);
    __publicField(this, "backoffDelay", INITIAL_BACKOFF_DELAY);
    __publicField(this, "lastKnownStatus", /* @__PURE__ */ new Map());
    __publicField(this, "showToastCallback");
    __publicField(this, "currentAppComponents", []);
    __publicField(this, "appType", "spaces-ui");
  }
  startMonitoring(showToastCallback, appType) {
    if (this.isMonitoring) return;
    this.showToastCallback = showToastCallback;
    this.appType = appType;
    this.detectRegion();
    this.isMonitoring = true;
    this.scheduleNextCheck();
  }
  stopMonitoring() {
    if (this.checkInterval) {
      clearTimeout(this.checkInterval);
      this.checkInterval = null;
    }
    this.isMonitoring = false;
    this.resetBackoff();
  }
  scheduleNextCheck(delay = POLLING_INTERVAL) {
    if (!this.isMonitoring) return;
    this.checkInterval = setTimeout(() => {
      this.checkServiceHealth();
    }, delay);
  }
  resetBackoff() {
    this.backoffDelay = INITIAL_BACKOFF_DELAY;
  }
  getNextBackoffDelay() {
    const currentDelay = this.backoffDelay;
    this.backoffDelay = Math.min(
      currentDelay * BACKOFF_MULTIPLIER,
      MAX_BACKOFF_DELAY
    );
    return currentDelay;
  }
  detectRegion() {
    try {
      if (typeof window !== "undefined" && window.location) {
        const hostname = window.location.hostname;
        const pathname = window.location.pathname;
        if (pathname.includes("/space/")) {
          this.appType = "spaces-ui";
        } else if (pathname.includes("/dashboard/")) {
          this.appType = "new-dashboard";
        } else {
          this.appType = "spaces-ui";
        }
        const regionalMapping = REGIONAL_COMPONENT_MAPPING[hostname];
        if (regionalMapping && regionalMapping[this.appType]) {
          this.currentAppComponents = regionalMapping[this.appType];
        } else {
          this.currentAppComponents = this.appType === "spaces-ui" ? ["Spaces"] : ["Dashboard"];
        }
        return;
      }
    } catch (error) {
    }
    this.currentAppComponents = this.appType === "spaces-ui" ? ["Spaces"] : ["Dashboard"];
  }
  async checkServiceHealth() {
    try {
      const response = await fetch(STATUS_API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (!response.ok) {
        throw new Error(`Status API request failed: ${response.status}`);
      }
      const data = await response.json();
      this.resetBackoff();
      const relevantComponents = data.components.filter((component) => {
        return !component.group && this.currentAppComponents.includes(component.name);
      });
      const degradedComponents = relevantComponents.filter(
        (comp) => comp.status === "degraded_performance" || comp.status === "partial_outage" || comp.status === "major_outage"
      );
      if (degradedComponents.length > 0) {
        const newDegradations = degradedComponents.filter((comp) => {
          const lastStatus = this.lastKnownStatus.get(comp.id);
          return lastStatus !== comp.status;
        });
        if (newDegradations.length > 0) {
          this.showDegradedServiceNotification(
            this.showToastCallback,
            newDegradations
          );
        }
        degradedComponents.forEach((comp) => {
          this.lastKnownStatus.set(comp.id, comp.status);
        });
        this.scheduleNextCheck();
        return;
      }
      relevantComponents.forEach((comp) => {
        this.lastKnownStatus.set(comp.id, comp.status);
      });
      this.scheduleNextCheck();
      return;
    } catch (error) {
      console.error("Error checking service health:", error);
      const delay = this.getNextBackoffDelay();
      this.scheduleNextCheck(delay);
      return;
    }
  }
  showDegradedServiceNotification(showToast, degradedComponents) {
    const hasMajorOutage = degradedComponents == null ? void 0 : degradedComponents.some(
      (comp) => comp.status === "major_outage"
    );
    const hasPartialOutage = degradedComponents == null ? void 0 : degradedComponents.some(
      (comp) => comp.status === "partial_outage"
    );
    const heading = hasMajorOutage ? "Major Outage" : hasPartialOutage ? "Partial Outage" : "Service Degradation";
    const message = hasMajorOutage ? "You may experience service unavailability" : hasPartialOutage ? "You may experience some service unavailability" : "You may experience degraded service performance";
    if (showToast) {
      showToast({
        heading,
        message,
        action: {
          label: "Check Status",
          onClick: () => window.open(
            "https://status.flatfile.io",
            "_blank",
            "noopener,noreferrer"
          )
        }
      });
    } else {
      console.warn("Status service notification:", message);
    }
  }
}
const statusService = new StatusService();
export {
  statusService
};
